/**
 * Progressive enhancement for the filter and sort controls.
 *
 * Without JS the markup is a plain GET form with <details> disclosures, so
 * everything still works. This layer adds:
 *  - one filter dropdown open at a time on desktop, where panels overlap
 *  - Escape and outside-click dismissal with focus returned to the toggle
 *  - sort submits on change, so Apply is only ever about filters
 *  - empty price inputs stripped so applied URLs stay clean
 *  - a busy state on Apply, and the panel reopens after the page reloads
 */
class FacetsShell extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form.facets');
    this.root = this.querySelector('[data-facets-root]');
    this.panels = Array.from(this.querySelectorAll('details.facet'));
    this.desktop = matchMedia('(min-width: 48rem)');

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.bindPanels();
    this.bindSort();
    this.bindSubmit();
    this.restoreOpenState();

    document.addEventListener('click', this.onDocumentClick);
    this.addEventListener('keydown', this.onKeydown);

    // Layout rules differ per breakpoint, so reset transient state when it changes.
    this.desktop.addEventListener('change', () => {
      this.closeOpenPanels(false);
      this.toggleSort(false);
      this.querySelectorAll('.facet__values--flip').forEach((panel) => panel.classList.remove('facet__values--flip'));
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  /** Only one overlapping dropdown at a time. On mobile the panels stack, so leave them alone. */
  bindPanels() {
    this.panels.forEach((panel) => {
      panel.addEventListener('toggle', () => {
        if (!panel.open) return;
        if (this.sortOpen) this.toggleSort(false);
        if (!this.desktop.matches) {
          // Inside the mobile sheet, bring the group heading into view.
          panel.querySelector('summary')?.scrollIntoView({ block: 'nearest' });
          return;
        }
        this.panels.forEach((other) => {
          if (other !== panel) other.open = false;
        });
        this.alignPanel(panel);
      });
    });
  }

  /**
   * Opens the panel leftwards only when it would actually overflow the viewport.
   * Measured rather than guessed from the toggle's position in the row.
   */
  alignPanel(panel) {
    const values = panel.querySelector('.facet__values');
    if (!values) return;
    values.classList.remove('facet__values--flip');
    const bounds = values.getBoundingClientRect();
    const margin = 12;
    if (bounds.right > window.innerWidth - margin && bounds.width < window.innerWidth - margin * 2) {
      values.classList.add('facet__values--flip');
    }
  }

  bindSort() {
    const select = this.querySelector('.facets__sort select');
    if (!select) return;
    select.addEventListener('change', () => this.submit());
    this.enhanceSort(select);
  }

  /**
   * Swaps the native select for a themed listbox. The select stays in the form so
   * its value still submits, and if this never runs the native control is used.
   */
  enhanceSort(select) {
    const label = select.closest('.facets__sort');
    if (!label || label.dataset.enhanced === 'true') return;
    label.dataset.enhanced = 'true';

    // The label's own text nodes become the caption; option text lives in the select.
    const captionText = Array.from(label.childNodes)
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .map((node) => node.textContent.trim())
      .filter(Boolean)
      .join(' ');
    Array.from(label.childNodes)
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .forEach((node) => node.remove());

    // The native control is hidden but still submits, so the label must not point at it.
    label.removeAttribute('for');
    label.classList.add('facets__sort--enhanced');

    const shell = document.createElement('div');
    shell.className = 'theme-select';

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'theme-select__toggle';
    toggle.setAttribute('aria-haspopup', 'listbox');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', `${select.id}-list`);

    // Caption sits inside the pill so the control reads like the filter toggles.
    const caption = document.createElement('span');
    caption.className = 'theme-select__caption';
    caption.id = `${select.id}-caption`;
    caption.textContent = captionText;

    const value = document.createElement('span');
    value.className = 'theme-select__value';
    value.id = `${select.id}-value`;
    value.textContent = select.options[select.selectedIndex]?.textContent ?? '';

    toggle.append(caption, value);
    toggle.insertAdjacentHTML(
      'beforeend',
      '<svg class="icon theme-select__caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m5 9 7 7 7-7"/></svg>'
    );
    toggle.setAttribute('aria-labelledby', `${caption.id} ${value.id}`);

    const list = document.createElement('ul');
    list.className = 'theme-select__list';
    list.id = `${select.id}-list`;
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-labelledby', caption.id);
    list.hidden = true;

    Array.from(select.options).forEach((option, index) => {
      const item = document.createElement('li');
      item.className = 'theme-select__option';
      item.setAttribute('role', 'option');
      item.id = `${select.id}-option-${index}`;
      item.dataset.value = option.value;
      item.setAttribute('aria-selected', index === select.selectedIndex ? 'true' : 'false');
      // A radio-style marker mirrors the checkbox column in the filter panels.
      item.innerHTML = '<span class="theme-select__mark" aria-hidden="true"></span>';
      item.append(document.createTextNode(option.textContent));
      list.append(item);
    });

    shell.append(toggle, list);
    label.append(shell);

    this.sort = { select, shell, toggle, list, value, options: Array.from(list.children) };
    this.activeIndex = select.selectedIndex;

    toggle.addEventListener('click', () => this.toggleSort());
    toggle.addEventListener('keydown', (event) => this.onToggleKeydown(event));
    list.addEventListener('keydown', (event) => this.onListKeydown(event));
    list.addEventListener('click', (event) => {
      const option = event.target instanceof Element ? event.target.closest('.theme-select__option') : null;
      if (option) this.commitSort(this.sort.options.indexOf(option));
    });
  }

  get sortOpen() {
    return this.sort ? this.sort.toggle.getAttribute('aria-expanded') === 'true' : false;
  }

  toggleSort(force) {
    if (!this.sort) return;
    const open = force ?? !this.sortOpen;
    this.sort.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    this.sort.list.hidden = !open;
    if (!open) return;
    this.closeOpenPanels(false);
    this.setActiveOption(this.sort.select.selectedIndex);
    this.sort.list.focus();
  }

  setActiveOption(index) {
    if (!this.sort) return;
    const total = this.sort.options.length;
    this.activeIndex = (index + total) % total;
    this.sort.options.forEach((option, i) => option.classList.toggle('is-active', i === this.activeIndex));
    const active = this.sort.options[this.activeIndex];
    this.sort.list.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({ block: 'nearest' });
  }

  commitSort(index) {
    if (!this.sort || index < 0) return;
    this.sort.select.selectedIndex = index;
    this.sort.value.textContent = this.sort.select.options[index].textContent;
    this.sort.options.forEach((option, i) => option.setAttribute('aria-selected', i === index ? 'true' : 'false'));
    this.toggleSort(false);
    this.sort.select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onToggleKeydown(event) {
    if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) return;
    event.preventDefault();
    this.toggleSort(true);
  }

  onListKeydown(event) {
    if (!this.sort) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.setActiveOption(this.activeIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.setActiveOption(this.activeIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.setActiveOption(0);
        break;
      case 'End':
        event.preventDefault();
        this.setActiveOption(this.sort.options.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.commitSort(this.activeIndex);
        break;
      case 'Tab':
        this.toggleSort(false);
        break;
      default:
        break;
    }
  }

  bindSubmit() {
    if (!this.form) return;
    this.form.addEventListener('submit', () => {
      this.clearEmptyPriceInputs();
      this.rememberOpenState();
      const apply = this.querySelector('[data-facets-apply]');
      if (!apply) return;
      apply.setAttribute('aria-busy', 'true');
      const label = apply.dataset.loadingLabel;
      const target = apply.querySelector('span');
      if (label && target) target.textContent = label;
    });
  }

  submit() {
    if (!this.form) return;
    if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
    else this.form.submit();
  }

  /** A blank price box would otherwise add an empty param to the URL. */
  clearEmptyPriceInputs() {
    this.querySelectorAll('.facet__price input').forEach((input) => {
      if (input.value.trim() === '') input.disabled = true;
    });
  }

  closeOpenPanels(returnFocus) {
    this.panels.forEach((panel) => {
      if (!panel.open) return;
      panel.open = false;
      if (returnFocus) panel.querySelector('summary')?.focus();
    });
  }

  onDocumentClick(event) {
    const inside = event.target instanceof Node && this.contains(event.target);
    if (inside) {
      // A click elsewhere in the shell still dismisses the sort listbox.
      if (this.sortOpen && this.sort && !this.sort.shell.contains(event.target)) this.toggleSort(false);
      return;
    }
    if (this.sortOpen) this.toggleSort(false);
    if (this.desktop.matches) this.closeOpenPanels(false);
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    if (this.sortOpen) {
      event.stopPropagation();
      this.toggleSort(false);
      this.sort.toggle.focus();
      return;
    }
    const openPanel = this.panels.find((panel) => panel.open);
    if (openPanel) {
      event.stopPropagation();
      openPanel.open = false;
      openPanel.querySelector('summary')?.focus();
      return;
    }
    if (this.root?.open && !this.desktop.matches) {
      this.root.open = false;
      this.root.querySelector('summary')?.focus();
    }
  }

  get storageKey() {
    return `sugar:facets-open:${location.pathname}`;
  }

  rememberOpenState() {
    try {
      const state = { open: Boolean(this.root?.open), scroll: Math.round(window.scrollY) };
      sessionStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch (error) {
      /* Private browsing can block sessionStorage. The panel just starts closed. */
    }
  }

  /**
   * Applying a filter is a full page load, which would otherwise drop the visitor
   * at the top of the page with the panel shut. Restore both, once.
   */
  restoreOpenState() {
    if (!this.root) return;
    let state;
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      if (!raw) return;
      sessionStorage.removeItem(this.storageKey);
      state = JSON.parse(raw);
    } catch (error) {
      return;
    }
    if (!state || typeof state !== 'object') return;
    if (state.open) this.root.open = true;
    if (typeof state.scroll === 'number' && state.scroll > 0) {
      requestAnimationFrame(() => window.scrollTo({ top: state.scroll, behavior: 'auto' }));
    }
  }
}

customElements.define('facets-shell', FacetsShell);
